import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Modal, Popconfirm, Table } from 'antd';
import {
    ArrowLeftOutlined,
    CreditCardOutlined,
    DatabaseFilled,
    DeleteOutlined,
    EditOutlined,
    GlobalOutlined,
    MessageOutlined,
    PhoneOutlined,
    ShoppingCartOutlined,
} from '@ant-design/icons';
import { Catcher, LoadingApiByIds } from '../';
import cx from 'classnames';
import { IPostPackage, NotificationType } from '../../interfaces';
import { useProfile, useProfileIsRight, useRouterStore, useTariffPackage } from '../../hooks';
import {
    UnitTypeValues,
    addNotification,
    apiHandleErrorMessages,
    deleteTariffPackage,
    getCompaniesSelect,
    getCountriesSelect,
    getOperatorsSelect,
    getTariffPackage,
    updateTariffPackage,
} from '../../middleware';
import { showDataWithUnit, showOriginalUnitByType, stringMoney } from '../../instruments';
import { ITrField, tB, tF, tI, tP, tT } from '../../translate';
import { CardBadge } from './Components';
import { AssignTariffModal } from './Components/AssignTariffModal/AssignTatiffModal';
import { CreatePackageForm } from './Components/PackageStep/CreatePackageForm';
import CardStyles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

const TariffsPackagesItemBox = ({ Icon, title, value }: { Icon?: React.FC; title: ITrField; value?: string }) => {
    const rawValue = value?.split(' ') || ['0'];

    if (rawValue[0] === '0') {
        return null;
    }

    return (
        <div className={TariffsStyles.tariffsPackagesItemBox}>
            <div className={TariffsStyles.title}>{tF(title)}</div>
            <div className={TariffsStyles.value}>
                {Icon && <Icon />}
                {value}
            </div>
        </div>
    );
};

export const TariffsPackagesItem: React.FC = observer(() => {
    const [isOpen, setIsOpen] = useState(false);
    const companyId = useProfile().companyId;
    const packageItem = useTariffPackage();
    const routerStore = useRouterStore();
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const currentRouteId = routerStore.routerState.params.id;
    const isLord = useProfileIsRight('LORD');

    const {
        name,
        packageType,
        dataVolume,
        dataVolumeUnit,
        smsVolume,
        voiceVolume = 0,
        tariffs,
        operators,
        networks,
        price,
        companies,
        cardsCount,
    } = packageItem;

    const goTo = (event: React.MouseEvent): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();

            routerStore.goTo('TariffsPackages').then();
        }
    };

    const openStepper = () => {
        setIsOpen(true);
    };

    const closeStepper = () => {
        setIsOpen(false);
    };

    const handleModalCancel = () => {
        Modal.confirm({
            title: tT('Close Dialog?'),
            content: tT('Are you sure you want to close this dialog? Any unsaved changes will be lost.'),
            okText: tB('Yes'),
            cancelText: tB('No'),
            onOk() {
                closeStepper();
            },
            wrapClassName: 'modal',
        });
    };

    const tableColumns = [
        {
            title: tF('Tariff Name'),
            dataIndex: 'name',
        },
    ];

    const handleDeleteTariffPackage = async () => {
        await deleteTariffPackage(packageItem.id).then(() => {
            routerStore.goTo('TariffsPackages').then();
        });
    };

    const handleSavePackage = async (packageData: IPostPackage) => {
        const { isError, resp } = await updateTariffPackage(currentRouteId, packageData);

        if (isError) {
            apiHandleErrorMessages(resp || {});

            return;
        }

        addNotification({
            type: NotificationType.SUCCESS,
            message: 'Edited package successfully.',
            isUsersError: true,
        });

        await getTariffPackage(currentRouteId).then();

        closeStepper();
    };

    const openAssignStepper = () => {
        setIsAssignOpen(true);
    };

    const closeAssignStepper = () => {
        setIsAssignOpen(false);
    };

    const getInitialValues = (): IPostPackage => {
        return {
            name: packageItem.name || '',
            packageType: packageType || 'extra',
            dataVolume: packageItem.dataVolume || 0,
            dataVolumeUnit: packageItem.dataVolumeUnit || 'mb',
            smsVolume: packageItem.smsVolume || 0,
            voiceVolume: packageItem.voiceVolume || 0,
            priceCents: packageItem.priceCents || 0,
            productId: packageItem.productId,
            planType: packageItem.planType,
            companyId: packageItem.company?.id || '',
            countryIds: packageItem?.countries?.map((country) => parseInt(country.id)) || [],
            operatorIds: packageItem?.operators?.map((operator) => parseInt(operator.id)) || [],
            networkIds: packageItem?.networks?.map((network) => parseInt(network.id)) || [],
            companyIds: packageItem?.companies?.map((company) => parseInt(company.id)) || [],
        };
    };

    useEffect(() => {
        if (currentRouteId) {
            getTariffPackage(currentRouteId).then();
            getOperatorsSelect().then();
            getCountriesSelect().then();
            if (isLord) {
                getCompaniesSelect().then();
            }
        }
    }, [currentRouteId, companyId]);

    const tableData = tariffs?.map((tariff) => {
        return {
            key: tariff.id,
            name: <div>{tariff.name}</div>,
        };
    });

    return (
        <Catcher>
            <div className={cx(CardStyles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffsPackagesItem}>
                    <Button onClick={(e) => goTo(e)} type={'ghost'}>
                        <ArrowLeftOutlined /> {tB('Back To Packages')}
                    </Button>

                    <div className={TariffsStyles.tariffsPackagesItemContainer}>
                        <div className={TariffsStyles.title1}>
                            <h1>{name}</h1>
                            <CardBadge name={packageType?.toUpperCase() || 'LOCAL'} />
                        </div>
                        <div className={TariffsStyles.buttonsContainer}>
                            <Button onClick={openStepper}>
                                <EditOutlined /> {tP('Edit Package')}
                            </Button>
                            {packageType === 'extra' ? (
                                <Button type={'primary'} onClick={openAssignStepper}>
                                    <ShoppingCartOutlined /> {tB('Buy Package')}
                                </Button>
                            ) : null}
                            <Popconfirm
                                placement="top"
                                title={tT(
                                    'Are you sure you want to delete this package? This action cannot be undone.',
                                )}
                                onConfirm={handleDeleteTariffPackage}
                                okText={tB('Yes')}
                                cancelText={tB('No')}
                            >
                                <Button type={'primary'} danger>
                                    <DeleteOutlined />
                                    {tB('Delete')}
                                </Button>
                            </Popconfirm>
                        </div>
                    </div>
                    <Divider />
                    <div className={TariffsStyles.itemBoxesContainer}>
                        <TariffsPackagesItemBox
                            Icon={DatabaseFilled}
                            title={'Company'}
                            value={packageItem.company?.name || 'No Company'}
                        />
                        {packageType === 'extra' ? (
                            <TariffsPackagesItemBox title={'Price'} value={stringMoney(price, 'EUR')} />
                        ) : null}
                        {(packageItem.productId || packageItem.planType) && (
                            <>
                                {packageItem.productId && (
                                    <TariffsPackagesItemBox title={'Product ID'} value={packageItem.productId} />
                                )}
                                {packageItem.planType && (
                                    <TariffsPackagesItemBox title={'Plan Type'} value={packageItem.planType} />
                                )}
                            </>
                        )}
                        <TariffsPackagesItemBox
                            Icon={DatabaseFilled}
                            title={'Data Allowance'}
                            value={showDataWithUnit(dataVolume, dataVolumeUnit)}
                        />
                        <TariffsPackagesItemBox
                            Icon={PhoneOutlined}
                            title={'Call Allowance'}
                            value={showOriginalUnitByType(`${voiceVolume}`, UnitTypeValues.VOICE)}
                        />
                        <TariffsPackagesItemBox
                            Icon={MessageOutlined}
                            title={'SMS Allowance'}
                            value={showOriginalUnitByType(`${smsVolume}`, UnitTypeValues.SMS)}
                        />
                        <div className={TariffsStyles.tariffsPackagesItemBox}>
                            <div className={TariffsStyles.used}>{tF('Used By Tariffs')}</div>
                            <div className={TariffsStyles.cards}>
                                <CreditCardOutlined />
                                {cardsCount || 0}
                            </div>
                        </div>
                    </div>
                    {packageType === 'extra' ? (
                        <div className={TariffsStyles.tariffsPackagesItemBox}>
                            <h3 className={TariffsStyles.header3}>
                                <GlobalOutlined className={TariffsStyles.icon} />
                                {tF('Assigned Companies')}
                            </h3>
                            <div className={TariffsStyles.tariffsPackagesItemOperator}>
                                {companies?.map((company) => (
                                    <span className={TariffsStyles.tariffsPackagesItemOperatorItem} key={company.id}>
                                        {company.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    <div className={TariffsStyles.tariffsPackagesItemBox}>
                        <h3 className={TariffsStyles.header3}>
                            <GlobalOutlined className={TariffsStyles.icon} />
                            {tF('Operator')}
                        </h3>
                        <div className={TariffsStyles.tariffsPackagesItemOperator}>
                            {operators?.map((operator) => (
                                <span className={TariffsStyles.tariffsPackagesItemOperatorItem} key={operator.id}>
                                    {operator.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className={TariffsStyles.tariffsPackagesItemBox}>
                        <h3 className={TariffsStyles.header3}>
                            <GlobalOutlined className={TariffsStyles.icon} />
                            {tF('Networks')}
                        </h3>
                        <div className={TariffsStyles.tariffsPackagesItemOperator}>
                            {networks?.map((network) => (
                                <span className={TariffsStyles.tariffsPackagesItemOperatorItem} key={network.id}>
                                    {network.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    {packageType !== 'local' && packageType !== 'extra' ? (
                        <div className={TariffsStyles.tariffsPackagesItemBox}>
                            <h3 className={TariffsStyles.header3}>{tI('Tariffs Using This Package')}</h3>
                            <Table
                                className={'table-theme'}
                                columns={tableColumns}
                                dataSource={tableData || []}
                                pagination={false}
                                size={'middle'}
                            />
                        </div>
                    ) : null}
                    <Modal
                        title={tP('Edit Package')}
                        open={isOpen}
                        onCancel={handleModalCancel}
                        wrapClassName={'modal'}
                        footer={null}
                        destroyOnClose
                    >
                        <div className={TariffsStyles.packageStep}>
                            <CreatePackageForm
                                onBack={closeStepper}
                                packageType={packageType || 'extra'}
                                onSave={handleSavePackage}
                                initialValues={getInitialValues()}
                            />
                        </div>
                    </Modal>
                    <AssignTariffModal
                        isOpen={isAssignOpen}
                        closeStepper={closeAssignStepper}
                        tariffPackage={packageItem}
                    />
                    <LoadingApiByIds idSpinners={['GET_TARIFF']} />
                </div>
            </div>
        </Catcher>
    );
});
